import { useState } from 'react';
import { api } from '../lib/api';
import { Field, Pill, useResource, useToast } from './ui';
import { day, dayTime } from '../lib/format';

/**
 * Erasure on a rights request: the processor confirmations opened when the
 * request was taken up, the proof of erasure the close needs, and that proof
 * once recorded. Used by the Rights requests page.
 */

export interface ErasureTask {
  id: string;
  processorName: string;
  channel: string;
  toldAt: string;
  dueAt: string | null;
  confirmedAt: string | null;
  confirmedBy: string | null;
  delivery: { status: string; deliveredAt: string | null } | null;
  status: 'confirmed' | 'outstanding';
  overdue: boolean;
}

export interface ErasureStatus {
  takenUp: boolean;
  tasks: ErasureTask[];
  confirmed: number;
  outstanding: number;
  proof: Record<string, unknown> | null;
}

export interface ProofDraft { systems: string; method: string; methodNote: string; erasedOn: string; confirmedBy: string }

export const METHODS: Array<[string, string]> = [
  ['deleted', 'Deleted'], ['anonymised', 'Anonymised'], ['overwritten', 'Overwritten'],
  ['key_destroyed', 'Encryption key destroyed'], ['other', 'Other'],
];

export const OVERRIDE_MIN = 10;

export function emptyProof(systems: string[] = []): ProofDraft {
  return { systems: systems.join(', '), method: 'deleted', methodNote: '', erasedOn: new Date().toISOString().slice(0, 10), confirmedBy: '' };
}

export function proofBody(p: ProofDraft) {
  return {
    systems: p.systems.split(',').map((s) => s.trim()).filter(Boolean),
    method: p.method,
    ...(p.methodNote.trim() ? { methodNote: p.methodNote.trim() } : {}),
    erasedOn: p.erasedOn,
    confirmedBy: p.confirmedBy.trim(),
  };
}

export function proofComplete(p: ProofDraft): boolean {
  const b = proofBody(p);
  return b.systems.length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(b.erasedOn) && b.confirmedBy.length > 0;
}

export function useErasure(requestId: string, enabled: boolean) {
  return useResource<ErasureStatus | null>(
    () => (enabled ? api.get<ErasureStatus>(`/v1/requests/${requestId}/erasure`) : Promise.resolve(null)),
    [requestId, enabled],
  );
}

const CHANNEL: Record<string, string> = { webhook: 'Webhook', manual: 'Told by email, phone or letter' };

export function ErasureConfirmations({ requestId, status, canHandle, onChanged }: {
  requestId: string; status: ErasureStatus; canHandle: boolean; onChanged: () => void;
}) {
  const toast = useToast();
  const [recording, setRecording] = useState<string | null>(null);
  const [by, setBy] = useState('');
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  async function confirm(taskId: string) {
    setBusy(true);
    try {
      await api.post(`/v1/requests/${requestId}/erasure/tasks/${taskId}/confirm`, {
        confirmedAt: new Date(`${on}T12:00:00`).toISOString(), confirmedBy: by.trim(),
      });
      toast('Confirmation recorded.', 'ok');
      setRecording(null);
      setBy('');
      onChanged();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  if (!status.tasks.length) {
    return <p className="small muted">{status.takenUp ? 'No processor is linked to this person\'s purposes.' : 'Processors are told when the request is acknowledged.'}</p>;
  }
  return (
    <table className="data">
      <thead><tr><th>Processor</th><th>Told</th><th>Due</th><th>Status</th><th /></tr></thead>
      <tbody>
        {status.tasks.map((t) => (
          <tr key={t.id}>
            <td>{t.processorName}</td>
            <td className="small">
              {CHANNEL[t.channel] ?? t.channel}, {dayTime(t.toldAt)}
              {t.delivery && <div className="muted">Delivery {t.delivery.status}{t.delivery.deliveredAt ? `, ${dayTime(t.delivery.deliveredAt)}` : ''}</div>}
            </td>
            <td className="small">{t.dueAt ? day(t.dueAt) : '—'}</td>
            <td>
              {t.status === 'confirmed'
                ? <span className="small">Confirmed {day(t.confirmedAt)} by {t.confirmedBy}</span>
                : <Pill tone={t.overdue ? 'bad' : 'warn'}>{t.overdue ? 'Overdue' : 'Outstanding'}</Pill>}
            </td>
            <td className="right">
              {canHandle && t.status === 'outstanding' && recording !== t.id && (
                <button className="small" onClick={() => { setRecording(t.id); setBy(''); }}>Record confirmation</button>
              )}
              {recording === t.id && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <input type="date" value={on} onChange={(e) => setOn(e.target.value)} style={{ width: 'auto' }} />
                  <input type="text" placeholder="Confirmed by" value={by} onChange={(e) => setBy(e.target.value)} style={{ width: 160 }} />
                  <button className="small primary" disabled={busy || !by.trim()} onClick={() => confirm(t.id)}>Save</button>
                  <button className="small" onClick={() => setRecording(null)}>Cancel</button>
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ProofForm({ value, onChange, outstanding, override, onOverride }: {
  value: ProofDraft; onChange: (p: ProofDraft) => void; outstanding: number; override: string; onOverride: (v: string) => void;
}) {
  const set = (patch: Partial<ProofDraft>) => onChange({ ...value, ...patch });
  return (
    <>
      <div className="row">
        <Field label="Systems erased" help="Comma separated.">
          <input type="text" value={value.systems} onChange={(e) => set({ systems: e.target.value })} />
        </Field>
        <Field label="Method">
          <select value={value.method} onChange={(e) => set({ method: e.target.value })}>
            {METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
      </div>
      {value.method === 'other' && (
        <Field label="Method, described">
          <input type="text" value={value.methodNote} onChange={(e) => set({ methodNote: e.target.value })} />
        </Field>
      )}
      <div className="row">
        <Field label="Erased on">
          <input type="date" value={value.erasedOn} onChange={(e) => set({ erasedOn: e.target.value })} />
        </Field>
        <Field label="Confirmed by" help="The person or team that carried out or checked the erasure.">
          <input type="text" value={value.confirmedBy} onChange={(e) => set({ confirmedBy: e.target.value })} />
        </Field>
      </div>
      {outstanding > 0 && (
        <Field
          label="Override reason"
          help={`${outstanding} processor ${outstanding === 1 ? 'confirmation is' : 'confirmations are'} outstanding. Closing now needs a reason of at least ${OVERRIDE_MIN} characters, recorded on the ledger.`}
        >
          <textarea rows={2} value={override} onChange={(e) => onOverride(e.target.value)} />
        </Field>
      )}
    </>
  );
}

export function ProofView({ proof }: { proof: Record<string, unknown> }) {
  const method = METHODS.find(([k]) => k === proof.method)?.[1] ?? String(proof.method ?? '');
  return (
    <dl className="kv">
      <dt>Systems erased</dt><dd>{(proof.systems as string[] | undefined)?.join(', ') ?? '—'}</dd>
      <dt>Method</dt><dd>{method}{proof.methodNote ? `: ${String(proof.methodNote)}` : ''}</dd>
      <dt>Erased on</dt><dd>{day(String(proof.erasedOn ?? ''))}</dd>
      <dt>Confirmed by</dt><dd>{String(proof.confirmedBy ?? '—')}</dd>
      <dt>Processors</dt><dd>{String(proof.processorsConfirmed ?? 0)} confirmed, {String(proof.processorsOutstanding ?? 0)} outstanding at close</dd>
      {proof.overrideReason ? <><dt>Override reason</dt><dd>{String(proof.overrideReason)}</dd></> : null}
      <dt>Recorded</dt><dd>{dayTime(String(proof.recordedAt ?? ''))} by {String(proof.recordedBy ?? '')}</dd>
    </dl>
  );
}
